import Keycloak from "keycloak-js";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { env } from "@/app/env";
import { AuthContext, type AuthContextValue, type AuthStatus } from "@/auth/auth-context";

const baseUrl = import.meta.env.BASE_URL;
const silentCheckSsoPath = baseUrl === "/" ? "silent-check-sso.html" : `${baseUrl.replace(/\/$/, "")}/silent-check-sso.html`;

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const keycloakRef = useRef<Keycloak | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [userName, setUserName] = useState("");

  useEffect(() => {
    if (env.e2eAuthToken) {
      setUserName(env.e2eAuthUser);
      setStatus("authenticated");
      return;
    }

    let mounted = true;
    const keycloak = new Keycloak({
      url: env.keycloakUrl,
      realm: env.keycloakRealm,
      clientId: env.keycloakClientId
    });
    keycloakRef.current = keycloak;

    void keycloak
      .init({
        onLoad: "check-sso",
        pkceMethod: "S256",
        checkLoginIframe: false,
        silentCheckSsoFallback: false,
        silentCheckSsoRedirectUri: new URL(silentCheckSsoPath, window.location.origin).toString()
      })
      .then((authenticated) => {
        if (!mounted) {
          return;
        }
        if (!authenticated) {
          setStatus("anonymous");
          return;
        }
        const hasRole = env.requiredRoles.some((role) => keycloak.hasRealmRole(role) || keycloak.hasResourceRole(role));
        setUserName(readUserName(keycloak));
        setStatus(hasRole ? "authenticated" : "forbidden");
      })
      .catch(() => {
        if (mounted) {
          setStatus("unavailable");
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      tokenReady: status === "authenticated",
      userName,
      login: (redirectPath) => {
        void keycloakRef.current?.login({
          redirectUri: appURL(redirectPath ?? `${window.location.pathname}${window.location.search}`)
        });
      },
      logout: () => {
        void keycloakRef.current?.logout({
          redirectUri: appURL("/")
        });
      },
      getToken: async (forceRefresh = false) => {
        if (env.e2eAuthToken) {
          return env.e2eAuthToken;
        }
        const keycloak = keycloakRef.current;
        if (!keycloak || !keycloak.authenticated) {
          throw new Error("not authenticated");
        }
        await keycloak.updateToken(forceRefresh ? -1 : 30);
        if (!keycloak.token) {
          throw new Error("token unavailable");
        }
        return keycloak.token;
      }
    }),
    [status, userName]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function appURL(path: string) {
  const basePath = baseUrl === "/" ? "" : baseUrl.replace(/\/$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${window.location.origin}${basePath}${normalizedPath}`;
}

function readUserName(keycloak: Keycloak) {
  const parsed = keycloak.tokenParsed;
  if (!parsed) {
    return "";
  }
  const email = typeof parsed.email === "string" ? parsed.email : "";
  const preferred = typeof parsed.preferred_username === "string" ? parsed.preferred_username : "";
  return email || preferred || parsed.sub || "";
}
